import { useState, useEffect, useCallback } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { FontRulesPanel } from './FontRulesPanel'
import { ColorPalettePanel } from './ColorPalettePanel'
import { LogoRulesPanel } from './LogoRulesPanel'
import { StyleRulesPanel } from './StyleRulesPanel'
import { brandComplianceService } from '@/lib/api/services/brandCompliance'
import type {
  BrandGuidelineDetail,
  ComplianceStrictness,
  CreateFontRuleRequest,
  UpdateFontRuleRequest,
  CreateColorRuleRequest,
  UpdateColorRuleRequest,
  CreateLogoRuleRequest,
  UpdateLogoRuleRequest,
  CreateStyleRuleRequest,
  UpdateStyleRuleRequest,
  UpdateBrandGuidelineRequest,
} from '@/lib/api/types'
import {
  Type,
  Palette,
  Image,
  Heading,
  Settings2,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Save,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface BrandGuidelinesEditorProps {
  guidelineId: string
  onBack: () => void
  onUpdated: () => void
}

export function BrandGuidelinesEditor({
  guidelineId,
  onBack,
  onUpdated,
}: BrandGuidelinesEditorProps) {
  const [guideline, setGuideline] = useState<BrandGuidelineDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('typography')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    description: '',
    strictness: 'Moderate' as ComplianceStrictness,
    isActive: true,
    isDefault: false,
  })

  const loadGuideline = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await brandComplianceService.getGuideline(guidelineId)
      setGuideline(data)
      setSettingsForm({
        name: data.name,
        description: data.description || '',
        strictness: data.strictness,
        isActive: data.isActive,
        isDefault: data.isDefault,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guideline')
    } finally {
      setLoading(false)
    }
  }, [guidelineId])

  useEffect(() => {
    loadGuideline()
  }, [loadGuideline])

  const handleSaveSettings = async () => {
    if (!guideline) return

    setSaving(true)
    try {
      const updateData: UpdateBrandGuidelineRequest = {
        name: settingsForm.name,
        description: settingsForm.description || undefined,
        strictness: settingsForm.strictness,
        isActive: settingsForm.isActive,
        isDefault: settingsForm.isDefault,
      }
      await brandComplianceService.updateGuideline(guideline.id, updateData)
      await loadGuideline()
      onUpdated()
      setSettingsOpen(false)
    } catch (err) {
      console.error('Failed to save settings:', err)
    } finally {
      setSaving(false)
    }
  }

  // Font Rules handlers
  const handleAddFontRule = async (data: CreateFontRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.addFontRule(guideline.id, data)
    await loadGuideline()
  }

  const handleUpdateFontRule = async (ruleId: string, data: UpdateFontRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.updateFontRule(guideline.id, ruleId, data)
    await loadGuideline()
  }

  const handleDeleteFontRule = async (ruleId: string) => {
    if (!guideline) return
    await brandComplianceService.deleteFontRule(guideline.id, ruleId)
    await loadGuideline()
  }

  // Color Rules handlers
  const handleAddColorRule = async (data: CreateColorRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.addColorRule(guideline.id, data)
    await loadGuideline()
  }

  const handleUpdateColorRule = async (ruleId: string, data: UpdateColorRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.updateColorRule(guideline.id, ruleId, data)
    await loadGuideline()
  }

  const handleDeleteColorRule = async (ruleId: string) => {
    if (!guideline) return
    await brandComplianceService.deleteColorRule(guideline.id, ruleId)
    await loadGuideline()
  }

  // Logo Rules handlers
  const handleAddLogoRule = async (data: CreateLogoRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.addLogoRule(guideline.id, data)
    await loadGuideline()
  }

  const handleUpdateLogoRule = async (ruleId: string, data: UpdateLogoRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.updateLogoRule(guideline.id, ruleId, data)
    await loadGuideline()
  }

  const handleDeleteLogoRule = async (ruleId: string) => {
    if (!guideline) return
    await brandComplianceService.deleteLogoRule(guideline.id, ruleId)
    await loadGuideline()
  }

  // Style Rules handlers
  const handleAddStyleRule = async (data: CreateStyleRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.addStyleRule(guideline.id, data)
    await loadGuideline()
  }

  const handleUpdateStyleRule = async (ruleId: string, data: UpdateStyleRuleRequest) => {
    if (!guideline) return
    await brandComplianceService.updateStyleRule(guideline.id, ruleId, data)
    await loadGuideline()
  }

  const handleDeleteStyleRule = async (ruleId: string) => {
    if (!guideline) return
    await brandComplianceService.deleteStyleRule(guideline.id, ruleId)
    await loadGuideline()
  }

  const getStrictnessBadge = (strictness: ComplianceStrictness) => {
    const styles = {
      Strict: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      Moderate: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
      Lenient: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    }
    return <Badge className={cn('text-xs', styles[strictness])}>{strictness}</Badge>
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        <p className="text-neutral-500 dark:text-neutral-400">Loading brand guideline...</p>
      </div>
    )
  }

  if (error || !guideline) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-red-600 dark:text-red-400">{error || 'Guideline not found'}</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Guidelines
        </Button>
      </div>
    )
  }

  const totalRules =
    guideline.fontRuleCount +
    guideline.colorRuleCount +
    guideline.logoRuleCount +
    guideline.styleRuleCount

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon-sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {guideline.name}
              </h1>
              {getStrictnessBadge(guideline.strictness)}
              {guideline.isDefault && (
                <Badge variant="secondary" className="text-xs">
                  Default
                </Badge>
              )}
              {!guideline.isActive && (
                <Badge variant="outline" className="text-xs text-neutral-500">
                  Inactive
                </Badge>
              )}
            </div>
            {guideline.description && (
              <p className="text-neutral-500 dark:text-neutral-400 mt-1">
                {guideline.description}
              </p>
            )}
          </div>
        </div>
        <Button variant="outline" onClick={() => setSettingsOpen(true)}>
          <Settings2 className="h-4 w-4 mr-2" />
          Settings
        </Button>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
          <span className="text-sm text-neutral-600 dark:text-neutral-300">
            <span className="font-semibold text-neutral-900 dark:text-neutral-100">
              {totalRules}
            </span>{' '}
            total rules
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
          <Type className="h-4 w-4 text-neutral-500" />
          <span className="text-sm text-neutral-600 dark:text-neutral-300">
            {guideline.fontRuleCount} fonts
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
          <Palette className="h-4 w-4 text-neutral-500" />
          <span className="text-sm text-neutral-600 dark:text-neutral-300">
            {guideline.colorRuleCount} colors
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
          <Image className="h-4 w-4 text-neutral-500" />
          <span className="text-sm text-neutral-600 dark:text-neutral-300">
            {guideline.logoRuleCount} logos
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
          <Heading className="h-4 w-4 text-neutral-500" />
          <span className="text-sm text-neutral-600 dark:text-neutral-300">
            {guideline.styleRuleCount} styles
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="typography" className="gap-2">
            <Type className="h-4 w-4" />
            Typography
          </TabsTrigger>
          <TabsTrigger value="colors" className="gap-2">
            <Palette className="h-4 w-4" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="logos" className="gap-2">
            <Image className="h-4 w-4" />
            Logos
          </TabsTrigger>
          <TabsTrigger value="styles" className="gap-2">
            <Heading className="h-4 w-4" />
            Styles
          </TabsTrigger>
        </TabsList>

        <TabsContent value="typography" className="mt-6">
          <FontRulesPanel
            fontRules={guideline.fontRules}
            loading={false}
            onAdd={handleAddFontRule}
            onUpdate={handleUpdateFontRule}
            onDelete={handleDeleteFontRule}
          />
        </TabsContent>

        <TabsContent value="colors" className="mt-6">
          <ColorPalettePanel
            colorRules={guideline.colorRules}
            loading={false}
            onAdd={handleAddColorRule}
            onUpdate={handleUpdateColorRule}
            onDelete={handleDeleteColorRule}
          />
        </TabsContent>

        <TabsContent value="logos" className="mt-6">
          <LogoRulesPanel
            logoRules={guideline.logoRules}
            loading={false}
            onAdd={handleAddLogoRule}
            onUpdate={handleUpdateLogoRule}
            onDelete={handleDeleteLogoRule}
          />
        </TabsContent>

        <TabsContent value="styles" className="mt-6">
          <StyleRulesPanel
            styleRules={guideline.styleRules}
            loading={false}
            onAdd={handleAddStyleRule}
            onUpdate={handleUpdateStyleRule}
            onDelete={handleDeleteStyleRule}
          />
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={(open) => !open && setSettingsOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Guideline Settings</DialogTitle>
            <DialogDescription>
              Update the name, description, and enforcement settings for this guideline.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={settingsForm.name}
                onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={settingsForm.description}
                onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>

            <div className="space-y-2">
              <Label>Strictness Level</Label>
              <Select
                value={settingsForm.strictness}
                onValueChange={(value) =>
                  setSettingsForm({ ...settingsForm, strictness: value as ComplianceStrictness })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Strict">
                    <div>
                      <span className="font-medium">Strict</span>
                      <span className="text-xs text-neutral-500 ml-2">Block on any violation</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Moderate">
                    <div>
                      <span className="font-medium">Moderate</span>
                      <span className="text-xs text-neutral-500 ml-2">Block on errors only</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Lenient">
                    <div>
                      <span className="font-medium">Lenient</span>
                      <span className="text-xs text-neutral-500 ml-2">Show warnings only</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Enable compliance checking with this guideline
                </p>
              </div>
              <Switch
                checked={settingsForm.isActive}
                onCheckedChange={(checked) => setSettingsForm({ ...settingsForm, isActive: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
              <div>
                <Label>Default Guideline</Label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Use this guideline for all compliance checks by default
                </p>
              </div>
              <Switch
                checked={settingsForm.isDefault}
                onCheckedChange={(checked) => setSettingsForm({ ...settingsForm, isDefault: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving || !settingsForm.name.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
